import type { ChangeEvent } from 'react';
import type { FC } from '../../lib/teact/teact';
import React, {
  memo, useCallback, useEffect, useRef, useState,
} from '../../lib/teact/teact';
import { getActions, withGlobal } from '../../global';

import type { GlobalState } from '../../global/types';

import { IS_TOUCH_ENV } from '../../util/browser/windowEnvironment';
import { pick } from '../../util/iteratees';

import useLastCallback from '../../hooks/useLastCallback';
import useLang from '../../hooks/useLang';

import AvatarEditable from '../ui/AvatarEditable';
import Button from '../ui/Button';
import InputText from '../ui/InputText';

type StateProps = Pick<GlobalState, (
  'authIsLoading' | 'authErrorKey'
)>;

const AuthSsgRegister: FC<StateProps> = ({
  authIsLoading,
  authErrorKey,
}) => {
  const {
    ssgSignUp,
    returnToAuthPhoneNumber,
  } = getActions();

  const lang = useLang();

  // eslint-disable-next-line no-null/no-null
  const inputRef = useRef<HTMLInputElement>(null);
  const [firstName, setFirstName] = useState('');
  const [croppedFile, setCroppedFile] = useState<File | undefined>();
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (!IS_TOUCH_ENV && inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (authErrorKey) {
      const nextError = typeof authErrorKey === 'string' ? authErrorKey : lang.withRegular(authErrorKey);
      setError(nextError);
    }
  }, [authErrorKey, lang]);

  const handleFirstNameChange = useLastCallback((e: ChangeEvent<HTMLInputElement>) => {
    setFirstName(e.target.value);
    if (error) setError(undefined);
  });

  const handleAvatarChange = useCallback((file: File) => {
    setCroppedFile(file);
  }, []);

  const handleSubmit = useLastCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (authIsLoading) return;
    if (!firstName.trim()) {
      setError('请输入昵称');
      return;
    }
    ssgSignUp({ firstName: firstName.trim(), avatarFile: croppedFile });
  });

  const handleBack = useLastCallback(() => {
    returnToAuthPhoneNumber();
  });

  return (
    <div id="auth-registration-form" className="custom-scroll">
      <div className="auth-form">
        <h1>设置昵称</h1>
        <p className="note">设置你的昵称</p>
        <form className="form" action="" onSubmit={handleSubmit}>
          <AvatarEditable onChange={handleAvatarChange} />
          <InputText
            ref={inputRef}
            id="sign-up-name"
            label="昵称"
            value={firstName}
            error={error}
            onChange={handleFirstNameChange}
          />
          <Button
            type="submit"
            size="smaller"
            ripple
            isLoading={authIsLoading}
            loadingNoText
          >
            确定
          </Button>
          <Button
            type="button"
            size="smaller"
            isText
            onClick={handleBack}
          >
            返回登录
          </Button>
        </form>
      </div>
    </div>
  );
};

export default memo(withGlobal(
  (global): StateProps => {
    return {
      ...pick(global, [
        'authIsLoading',
        'authErrorKey',
      ]),
    };
  },
)(AuthSsgRegister));
